/**
 * Shared defaults and constants for PPA (Partner Platforms by Agoda) hotel components.
 *
 * `ppaHotelIFrameDefaults` — per-iframe-id defaults. The SPA's `ppaHotelIFrames` app-setting
 * entry (`ppaHotelIFrames.<iframeId>`) only needs to provide the env-varying pieces
 * (`iframeUrl`, `cid`). `layout` and `tag` fall back to the values here unless the app-setting
 * overrides them. Values follow the vendor recommendations in `SWA_Widget_Integration_Guide_PPA.pdf`
 * section 6 (tag pattern) and section 5 (layout mapping), except where noted below.
 *
 * Note: `confirmation-expired` is a SWA-specific iframe id not enumerated in the vendor
 * guide; its `layout`/`tag` values are conventions chosen by the SWA team and may be
 * revised once RTA provides guidance.
 *
 * `ppaHotelLoadingTemplate` — `LoadingDepiction` template used by PPA hotel form skeletons
 * while the vendor iframe signals readiness. Two rows: a single-column header block, then a
 * five-row grid using the hotel-booking-form skeleton color token so the placeholder blends
 * with the surrounding UI. Shared across all PPA hotel iframe ids so every iframe shows the
 * same skeleton during the pre-ready window.
 *
 * Search input defaults (`DEFAULT_ADULTS`, `DEFAULT_CURRENCY`) and validation bounds
 * (`ADULTS_MIN`/`ADULTS_MAX`, `CHILDREN_MIN`/`CHILDREN_MAX`, `ROOMS_MIN`/`ROOMS_MAX`) are used
 * by `buildIframeSrc` when serializing search criteria into iframe URL parameters — values
 * outside their range are dropped from the URL rather than clamped.
 *
 * Locale constants (`DEFAULT_LOCALE`, `ESPANOL_LOCALE`, `ESPANOL_HOSTNAME`) drive the locale
 * segment of the PPA iframe URL. `PROD_TIER` matches `getEnvironmentTier()`'s production
 * return value and selects between the `iframeUrl.prod` and `iframeUrl.staging` values in
 * the SPA's `ppaHotelIFrames` app-setting entry.
 */

export const DEFAULT_IFRAME_ID = "hotel-widget";

export const ppaHotelIFrameDefaults = {
  "confirmation-above-fold": {
    layout: "above_fold",
    tag: "flight_confirmation_above_fold",
  },
  "confirmation-below-fold": {
    layout: "below_fold",
    tag: "flight_confirmation_below_fold",
  },
  "confirmation-expired": {
    layout: "above_fold",
    tag: "flight_confirmation_expired",
  },
  [DEFAULT_IFRAME_ID]: {
    layout: "home_page_search",
    tag: "home_page_search",
  },
};

export const ppaHotelLoadingTemplate = [
  {
    bottomMargin: "medium",
    numberOfColumns: 1,
  },
  {
    bottomMargin: "medium",
    color: "hotel-booking-form-template-color",
    numberOfRows: 5,
  },
];

// Search input defaults
export const DEFAULT_ADULTS = 1;
export const DEFAULT_CURRENCY = "USD";

// Locale and environment tier
export const DEFAULT_LOCALE = "en-us";
export const ESPANOL_HOSTNAME = "espanol.southwest.com";
export const ESPANOL_LOCALE = "es";
export const PROD_TIER = "prod";

// Keys in the `ppaHotelIFrames` app-setting entry's `iframeUrl` object
export const IFRAME_URL_PROD_KEY = "prod";
export const IFRAME_URL_STAGING_KEY = "staging";

// Search validation bounds
export const ADULTS_MIN = 1;
export const ADULTS_MAX = 9;
export const CHILDREN_MIN = 0;
export const CHILDREN_MAX = 17;
export const ROOMS_MIN = 1;
export const ROOMS_MAX = 9;

// Iframe rendering
export const IFRAME_TITLE = "Hotel search iframe";
export const INITIAL_HEIGHT = 370;
export const READY_FALLBACK_DELAY = 1500;

// Iframe sandbox — least-privilege default for the vendor iframe.
// - `allow-scripts` and `allow-same-origin` are required for the vendor's JS + storage access.
// - `allow-forms` lets the vendor submit their search form.
// - `allow-popups` lets the vendor open booking flow in a new window when the user clicks
//   through; `allow-popups-to-escape-sandbox` keeps that popup unrestricted rather than
//   inheriting our sandbox chain.
// Deliberately omitted: `allow-top-navigation` (vendor can't hijack the parent page) and
// `allow-modals` (vendor can't fire `alert`/`confirm` on our page).
export const IFRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox";

// Vendor postMessage protocol markers
export const VENDOR_SOURCE = "hotel-cross-sell-widget-v2";
export const VENDOR_VERSION = 2;

export const EVENT_TYPES = {
  ERROR: "crossSellError",
  READY: "crossSellReady",
  RESIZE: "crossSellResize",
  UPDATE: "crossSellUpdate",
};
